import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsServiceProxy } from '@rci/self-serve-fido/core/analytics';
import { updateCCModalAnalyticsConfig } from '@rci/self-serve-fido/payments/analytics/update-cc-modal-analytics-config';
import { PromiseToPayService } from '@rci/self-serve-fido/promise-to-pay/promise-to-pay.service';
import { UserInformationStateFacade } from '@rci/self-serve-fido/state/user-information/user-information-state.facade';
import { ICompleteUniversalDataLayer } from '@rogers/analytics';
import { UserAccount } from '@rogers/self-serve-common';
import { AccountDetail, AccountListFacadeService, fetchAccountDetailSuccess } from '@rogers/self-serve-common/account';
import { AppAlert, AppAlertType, upsertAlert } from '@rogers/self-serve-common/alert';
import {
  AlertContentDetails,
  AlertContentFacadeService,
  fetchAlertContentSuccess,
} from '@rogers/self-serve-common/alert-content';
import {
  BillingDetail,
  BillingDetailEntity,
  BillingDetailFacadeService,
  PaymentMethodType,
  fetchBillingDetailFail,
  fetchBillingDetailSuccess,
} from '@rogers/self-serve-common/billing-detail';
import { CustomDatePipe } from '@rogers/self-serve-utils/custom-date';
import { combineLatest, of } from 'rxjs';
import { filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { earlySuspensionAnalyticsConfig } from '../billing-info/analytics/early-suspension-analytics-config';
import { EmptyAction } from './actions';
import { AlertId, EarlySuspensionFlag, UniqueAlertId } from './state.interface';

const COLLECTION_ACTIVITY_CODES = [
  EarlySuspensionFlag.COLLECTION_CODE_1,
  EarlySuspensionFlag.COLLECTION_CODE_C,
  EarlySuspensionFlag.COLLECTION_CODE_R,
  EarlySuspensionFlag.INTERNET,
];

const EARLY_SUSPENSION_CODES = [
  EarlySuspensionFlag.ACCOUNT,
  EarlySuspensionFlag.COLLECTION_CODE_1,
  EarlySuspensionFlag.COLLECTION_CODE_C,
  EarlySuspensionFlag.DATA,
  EarlySuspensionFlag.INTERNET,
];

@Injectable()
export class BillingAlertsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly accountListFacadeService: AccountListFacadeService,
    private readonly alertContentFacadeService: AlertContentFacadeService,
    private readonly analyticsServiceProxy: AnalyticsServiceProxy,
    private readonly billingDetailFacadeService: BillingDetailFacadeService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly customDatePipe: CustomDatePipe,
    private readonly promiseToPayService: PromiseToPayService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly userInformationStateFacade: UserInformationStateFacade,
  ) {}

  loadAutopayPromoAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchBillingDetailSuccess),
        mergeMap(({ billingDetail }) => {
          return of(billingDetail).pipe(
            withLatestFrom(this.accountListFacadeService.getAccount$(billingDetail.accountNumber)),
            filter(([_, account]) => this.canShowAutopayPromoAlert(billingDetail, account)),
            switchMap(([_, account]) => this.generateAutopayPromoAlert(account.accountNumber)),
          );
        }),
      ),
    { dispatch: true },
  );

  loadBillingDetailsErrorAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchBillingDetailFail),
        mergeMap(({ accountNumber }) => {
          return of(accountNumber).pipe(
            withLatestFrom(this.accountListFacadeService.getAccount$(accountNumber)),
            switchMap(() => this.generateBillingDetailsErrorAlerts(accountNumber)),
          );
        }),
      ),
    { dispatch: true },
  );

  loadCreditCardExpirationAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchBillingDetailSuccess),
        mergeMap(({ billingDetail }) => {
          return of(billingDetail).pipe(
            withLatestFrom(this.accountListFacadeService.getAccount$(billingDetail.accountNumber)),
            filter(([_, account]) => !account.isPrepaidAccount && !!billingDetail),
            switchMap(() => this.generateCreditCardExpirationAlerts(billingDetail)),
          );
        }),
      ),
    { dispatch: true },
  );

  loadCreditLimitAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchBillingDetailSuccess),
        mergeMap(({ billingDetail }) => {
          return of(billingDetail).pipe(
            withLatestFrom(this.accountListFacadeService.getAccount$(billingDetail.accountNumber)),
            filter(([billingDetail, account]) => this.canShowCreditLimitAlerts(billingDetail, account)),
            switchMap(([billingDetail, account]) => this.generateCreditLimitAlerts(billingDetail, account)),
          );
        }),
      ),
    { dispatch: true },
  );

  loadEarlySuspensionAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchAccountDetailSuccess),
        tap(({ accountDetail }) => {
          if (COLLECTION_ACTIVITY_CODES.includes(accountDetail.earlySuspensionFlag as EarlySuspensionFlag)) {
            this.trackCollectionActivity(accountDetail.earlySuspensionFlag);
          }
        }),
        filter(({ accountDetail }) =>
          EARLY_SUSPENSION_CODES.includes(accountDetail.earlySuspensionFlag as EarlySuspensionFlag),
        ),
        mergeMap(({ accountDetail }) =>
          this.generateEarlySuspensionAlerts(
            accountDetail.accountNumber,
            accountDetail.earlySuspensionFlag,
            accountDetail.earlySuspensionDate,
          ),
        ),
      ),
    { dispatch: true },
  );

  loadIPPAlerts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchBillingDetailSuccess),
        filter(({ billingDetail }) => billingDetail.displayActiveIpp || billingDetail.displayBrokenIpp),
        mergeMap(({ billingDetail }) => this.generateIPPAlerts(billingDetail)),
      ),
    { dispatch: true },
  );

  loadPastDueBalanceAlert$ = createEffect(
    () =>
      combineLatest([
        this.actions$.pipe(ofType(fetchAccountDetailSuccess)),
        this.actions$.pipe(ofType(fetchBillingDetailSuccess)),
      ]).pipe(
        filter(([{ accountDetail }, { billingDetail }]) =>
          this.canShowPastDueBalanceAlert(accountDetail, billingDetail),
        ),
        switchMap(([{ accountDetail }, _]) => this.generatePastDueBalanceAlert(accountDetail.accountNumber)),
      ),
    { dispatch: true },
  );

  loadPtpOrReportPaymentAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchAlertContentSuccess),
        withLatestFrom(this.accountListFacadeService.accountList$),
        mergeMap(([_, accountList]) => accountList),
        mergeMap(account => {
          return this.billingDetailFacadeService.getBillingDetail$(account.accountNumber).pipe(
            filter(billingDetail => this.canShowPtpOrReportPaymentAlert(account, billingDetail)),
            switchMap(billingDetail => this.generatePtpOrReportPaymentAlert(account, billingDetail)),
            take(1),
          );
        }),
      ),
    { dispatch: true },
  );

  loadSuspendedAccountAlert$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fetchAccountDetailSuccess),
        filter(({ accountDetail }) => accountDetail.isSuspendedAccount),
        mergeMap(({ accountDetail }) => this.generateSuspendedAccountAlert(accountDetail.accountNumber)),
      ),
    { dispatch: true },
  );

  private canShowAutopayPromoAlert(billingDetail: BillingDetail, account: UserAccount) {
    return (
      account.accountNumber === billingDetail.accountNumber &&
      !account.isCancelledAccount &&
      account.isWirelessOrConsolidatedAccount &&
      account.autoPayPromoEligInd === 'UWP' &&
      !this.userInformationStateFacade.isSubscriber &&
      !!billingDetail.paymentMethod &&
      billingDetail.paymentMethod.mopType === PaymentMethodType.R
    );
  }

  private canShowCreditLimitAlerts(billingDetail: BillingDetail, account: UserAccount) {
    return (
      account.accountNumber === billingDetail.accountNumber &&
      !!billingDetail.creditLimit &&
      (account.isActiveAccount || account.isSuspendedAccount) &&
      !account.isPrepaidAccount
    );
  }

  private canShowPastDueBalanceAlert(accountDetail: AccountDetail, billingDetail: BillingDetail) {
    const { earlySuspensionFlag, isWirelessOrConsolidatedAccount } = accountDetail;
    const { displayActiveIpp, displayPastDueBalanceIpp, pastDueBalance } = billingDetail;
    return (
      accountDetail.accountNumber === billingDetail.accountNumber &&
      (!earlySuspensionFlag ||
        (!!earlySuspensionFlag &&
          !EARLY_SUSPENSION_CODES.includes(accountDetail.earlySuspensionFlag as EarlySuspensionFlag))) &&
      isWirelessOrConsolidatedAccount &&
      ((!displayActiveIpp && pastDueBalance > 0 && !this.userInformationStateFacade.isSubscriber) ||
        displayPastDueBalanceIpp)
    );
  }

  private canShowPtpOrReportPaymentAlert(account: UserAccount, billingDetail: BillingDetailEntity) {
    return (
      this.router.url.includes('/overview') &&
      !!account &&
      !!account.alias &&
      account.accountNumber === billingDetail.accountNumber &&
      !account.isCancelledAccount &&
      !account.isTentative &&
      account.isWirelessOrConsolidatedAccount
    );
  }

  private generateAutopayPromoAlert(accountNumber: string) {
    return this.getAlertContent$().pipe(
      map(({ alerts }) => {
        const analyticsConfig = this.getDiscountBannerAnalyticsConfig();
        const uniqueAlertId = UniqueAlertId.AUTOPAY_PROMO;
        const alertContent = this.getAlertContentById(alerts, AlertId.AUTOPAY_PROMO);
        analyticsConfig.displayAlertTracking.additionalPayload.notification[0].name = 'setup automatic payment';

        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(accountNumber, uniqueAlertId, alertContent);
          analyticsConfig.displayAlertTracking.additionalPayload.notification[0].message = alertContent.copy.body;
          return upsertAlert({ alert: { ...alertObject, analyticsConfig } });
        }
        return EmptyAction();
      }),
    );
  }

  private generateBillingDetailsErrorAlerts(accountNumber: string) {
    return this.getAlertContent$().pipe(
      map(alerts => {
        let uniqueAlertId, alertContent: AlertContentDetails;
        uniqueAlertId = UniqueAlertId.BILLING_DETAILS_ERROR;
        alertContent = this.getAlertContentById(alerts.alerts, AlertId.BILLING_DETAILS_ERROR);

        if (this.userInformationStateFacade.isSubscriber) {
          uniqueAlertId = UniqueAlertId.BILLING_DETAILS_ERROR_SUBSCRIBER;
          alertContent = this.getAlertContentById(alerts.alerts, AlertId.BILLING_DETAILS_ERROR_SUBSCRIBER);
        }
        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(accountNumber, uniqueAlertId, alertContent, {
            ban: accountNumber,
          });
          return upsertAlert({ alert: { ...alertObject } });
        }
        return EmptyAction();
      }),
    );
  }

  /* eslint-disable */
  private generateCreditCardExpirationAlerts(billingDetail: BillingDetail) {
    if (billingDetail.paymentMethod?.mopType !== PaymentMethodType.C) {
      return of(EmptyAction());
    }

    const { ccExpiryDays, isCCExpired, isCCExpiring } = billingDetail.paymentMethod.creditCardDetail;
    if (!(isCCExpired || isCCExpiring)) {
      return of(EmptyAction());
    }
    return this.getAlertContent$().pipe(
      map(({ alerts }) => {
        const analyticsConfig = updateCCModalAnalyticsConfig.updateCCAlertAnalyticsConfig;
        let uniqueAlertId, alertContent: AlertContentDetails;
        if (isCCExpired) {
          analyticsConfig.displayAlertTracking.additionalPayload.notification[0].name = 'credit card expiry alert';
          uniqueAlertId = UniqueAlertId.CC_EXPIRED;
          alertContent = this.getAlertContentById(alerts, AlertId.CC_EXPIRED);
        }
        if (isCCExpiring) {
          analyticsConfig.displayAlertTracking.additionalPayload.notification[0].name = 'credit card expiring alert';
          uniqueAlertId = UniqueAlertId.CC_EXPIRING;
          alertContent = this.getAlertContentById(alerts, AlertId.CC_EXPIRING);
        }

        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(billingDetail.accountNumber, uniqueAlertId, alertContent, {
            ccExpiryDays,
          });
          analyticsConfig.displayAlertTracking.additionalPayload.notification[0].message =
            this.translateService.instant(alertObject.alertData.copy.body);
          analyticsConfig.displayAlertTracking.additionalPayload.notification[0].id = uniqueAlertId;
          return upsertAlert({ alert: { ...alertObject, analyticsConfig } });
        }
        return EmptyAction();
      }),
    );
  }

  private generateCreditLimitAlerts(billingDetail: BillingDetail, account: UserAccount) {
    return this.getAlertContent$().pipe(
      map(({ alerts }) => {
        const { creditLimitUsedPercentage, exceedCreditLimit } = billingDetail.creditLimit;

        let uniqueAlertId, alertContent: AlertContentDetails;
        if (exceedCreditLimit) {
          if (account.isActiveAccount) {
            uniqueAlertId = UniqueAlertId.EXCEEDED_CREDIT_LIMIT_ACTIVE_ACCOUNT;
            alertContent = this.getAlertContentById(alerts, AlertId.EXCEEDED_CREDIT_LIMIT_ACTIVE_ACCOUNT);
          }
          if (account.isSuspendedAccount) {
            uniqueAlertId = UniqueAlertId.EXCEEDED_CREDIT_LIMIT_SUSPENDED_ACCOUNT;
            alertContent = this.getAlertContentById(alerts, AlertId.EXCEEDED_CREDIT_LIMIT_SUSPENDED_ACCOUNT);
          }
        } else if (creditLimitUsedPercentage >= 90 && creditLimitUsedPercentage < 100) {
          uniqueAlertId = UniqueAlertId.CREDIT_LIMIT_AT_90PERCENT;
          alertContent = this.getAlertContentById(alerts, AlertId.CREDIT_LIMIT_AT_90PERCENT);
        } else if (creditLimitUsedPercentage >= 75 && creditLimitUsedPercentage < 90) {
          uniqueAlertId = UniqueAlertId.CREDIT_LIMIT_AT_75PERCENT;
          alertContent = this.getAlertContentById(alerts, AlertId.CREDIT_LIMIT_AT_75PERCENT);
        } else if (creditLimitUsedPercentage >= 60 && creditLimitUsedPercentage < 75) {
          uniqueAlertId = UniqueAlertId.CREDIT_LIMIT_AT_60PERCENT;
          alertContent = this.getAlertContentById(alerts, AlertId.CREDIT_LIMIT_AT_60PERCENT);
        }

        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(billingDetail.accountNumber, uniqueAlertId, alertContent);
          return upsertAlert({ alert: { ...alertObject } });
        }
        return EmptyAction();
      }),
    );
  }

  private generateEarlySuspensionAlerts(accountNumber: string, earlySuspensionFlag: string, earlySuspensionDate: Date) {
    return this.getAlertContent$().pipe(
      map(({ alerts }) => {
        let uniqueAlertId;
        let alertContent: AlertContentDetails;
        let alertType: string;
        const analyticsConfig = this.getSuspensionAnalyticsConfig();
        if (earlySuspensionFlag === EarlySuspensionFlag.ACCOUNT) {
          uniqueAlertId = UniqueAlertId.EARLY_ACCOUNT_SUSPENSION;
          alertContent = this.getAlertContentById(alerts, AlertId.EARLY_ACCOUNT_SUSPENSION);
          alertType = 'account suspension';
        } else if (earlySuspensionFlag === EarlySuspensionFlag.DATA) {
          uniqueAlertId = UniqueAlertId.EARLY_DATA_SUSPENSION;
          alertContent = this.getAlertContentById(alerts, AlertId.EARLY_DATA_SUSPENSION);
          alertType = 'data suspension';
        } else if (earlySuspensionFlag === EarlySuspensionFlag.COLLECTION_CODE_1) {
          uniqueAlertId = UniqueAlertId.EARLY_AGENCY_ASSIGNMENT;
          alertContent = this.getAlertContentById(alerts, AlertId.EARLY_AGENCY_ASSIGNMENT);
          alertType = 'agency assignment';
        } else if (earlySuspensionFlag === EarlySuspensionFlag.COLLECTION_CODE_C) {
          uniqueAlertId = UniqueAlertId.EARLY_ACCOUNT_CANCELLATION;
          alertContent = this.getAlertContentById(alerts, AlertId.EARLY_ACCOUNT_CANCELLATION);
          alertType = 'cancellation';
        } else if (earlySuspensionFlag === EarlySuspensionFlag.INTERNET) {
          uniqueAlertId = UniqueAlertId.INTERNET_SUSPENDED;
          alertContent = this.getAlertContentById(alerts, AlertId.EARLY_INTERNET_SUSPENSION);
          alertType = 'internet suspension';
        }

        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(accountNumber, uniqueAlertId, alertContent, {
            earlySuspensionDate: this.customDatePipe.transform(
              earlySuspensionDate,
              this.translateService.currentLang,
              'MMMM dd, yyyy',
              'dd MMMM yyyy',
            ),
          });

          analyticsConfig.displayAlertTracking.additionalPayload = this.getAdditionalTrackingPayload(
            `${alertType} notice`,
          );
          analyticsConfig.displayAlertTracking.additionalPayload.notification[0].message =
            alertObject.alertData.copy.body;
          analyticsConfig.alertCtaTracking.additionalPayload.interaction.name = `${alertType} notice | ${alertObject.alertData.cta[0].copy}`;

          return upsertAlert({ alert: { ...alertObject, analyticsConfig } });
        }
        return EmptyAction();
      }),
    );
  }

  private generateIPPAlerts(billingDetail: BillingDetail) {
    return this.getAlertContent$().pipe(
      map(({ alerts }) => {
        const { accountNumber, displayActiveIpp, ippBalance } = billingDetail;
        const isNegativeIpp = displayActiveIpp && ippBalance < 0;
        let uniqueAlertId, alertContent: AlertContentDetails;

        if (displayActiveIpp && !isNegativeIpp) {
          uniqueAlertId = UniqueAlertId.IPP_ACTIVE;
          alertContent = this.getAlertContentById(alerts, AlertId.IPP_ACTIVE);
        } else if (isNegativeIpp) {
          uniqueAlertId = UniqueAlertId.IPP_ACTIVE_NEGATIVE_BALANCE;
          alertContent = this.getAlertContentById(alerts, AlertId.IPP_ACTIVE_NEGATIVE_BALANCE);
        } else {
          uniqueAlertId = UniqueAlertId.IPP_BROKEN;
          alertContent = this.getAlertContentById(alerts, AlertId.IPP_BROKEN);
        }

        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(accountNumber, uniqueAlertId, alertContent);
          return upsertAlert({ alert: { ...alertObject } });
        }
        return EmptyAction();
      }),
    );
  }

  private generatePastDueBalanceAlert(accountNumber: string) {
    return this.getAlertContent$().pipe(
      map(({ alerts }) => {
        const uniqueAlertId = UniqueAlertId.PAST_DUE_BALANCE;
        const alertContent = this.getAlertContentById(alerts, AlertId.PAST_DUE_BALANCE);

        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(accountNumber, uniqueAlertId, alertContent);
          return upsertAlert({ alert: { ...alertObject } });
        }
        return EmptyAction();
      }),
    );
  }

  private generatePtpOrReportPaymentAlert(accountDetail: AccountDetail, billingDetail: BillingDetail) {
    return this.promiseToPayService.getPtpAndReportDetails$(accountDetail, billingDetail).pipe(
      switchMap(ptpAndReportDetails => {
        return this.getAlertContent$().pipe(
          map(({ alerts }) => {
            let alertContent: AlertContentDetails, amount: number, date: Date, uniqueAlertId: string;
            const {
              isSinglePtp,
              pastDueBalance,
              ptpDueDate,
              reportedAmount,
              reportedDate,
              setupPtp,
              showPtpAlert,
              showReportPaymentAlert,
            } = ptpAndReportDetails;

            if (showPtpAlert) {
              if (setupPtp) {
                uniqueAlertId = UniqueAlertId.PROMISE_TO_PAY_SETUP;
                alertContent = this.getAlertContentById(alerts, AlertId.PROMISE_TO_PAY_SETUP);
              } else {
                uniqueAlertId = UniqueAlertId.PROMISE_TO_PAY_INSTALLMENTS;
                if (isSinglePtp) {
                  alertContent = this.getAlertContentById(alerts, AlertId.PROMISE_TO_PAY_SINGLE_INSTALLMENT);
                } else {
                  alertContent = this.getAlertContentById(alerts, AlertId.PROMISE_TO_PAY_MULTI_INSTALLMENTS);
                }
                amount = pastDueBalance;
                date = ptpDueDate;
              }
            }

            if (showReportPaymentAlert) {
              uniqueAlertId = UniqueAlertId.REPORT_PAYMENT;
              alertContent = this.getAlertContentById(alerts, AlertId.REPORT_PAYMENT);
              amount = reportedAmount;
              date = reportedDate;
            }

            if (!!alertContent?.copy) {
              const alertObject = this.getAlertObject(accountDetail.accountNumber, uniqueAlertId, alertContent, {
                amount: this.currencyPipe.transform(
                  amount,
                  'CAD',
                  'symbol-narrow',
                  '1.2-2',
                  this.translateService.currentLang,
                ),
                date: this.customDatePipe.transform(
                  date,
                  this.translateService.currentLang,
                  'MMMM dd, yyyy',
                  'dd MMMM yyyy',
                ),
              });
              return upsertAlert({ alert: { ...alertObject } });
            }
            return EmptyAction();
          }),
        );
      }),
    );
  }

  private generateSuspendedAccountAlert(accountNumber: string) {
    return this.getAlertContent$().pipe(
      map(({ alerts }) => {
        const uniqueAlertId = UniqueAlertId.ACCOUNT_SUSPENDED;
        const alertContent = this.getAlertContentById(alerts, AlertId.ACCOUNT_SUSPENDED);

        const analyticsConfig = this.getSuspensionAnalyticsConfig();
        analyticsConfig.displayAlertTracking.additionalPayload = this.getAdditionalTrackingPayload('account suspended');

        if (!!alertContent?.copy) {
          const alertObject = this.getAlertObject(accountNumber, uniqueAlertId, alertContent);
          analyticsConfig.displayAlertTracking.additionalPayload.notification[0].message =
            alertObject.alertData.copy.body;
          analyticsConfig.alertCtaTracking.additionalPayload.interaction.name = `account suspended | ${alertObject.alertData.cta[0].copy}`;
          return upsertAlert({ alert: { ...alertObject, analyticsConfig } });
        }
        return EmptyAction();
      }),
    );
  }

  private getAlertContent$() {
    return this.translateService
      .stream('lang')
      .pipe(switchMap(lang => this.alertContentFacadeService.getAlertContentByLang$('billing-alerts', lang)));
  }

  private getAlertObject(
    key: string,
    uniqueAlertId: string,
    alertContent,
    replacements?,
    analyticsConfig = null,
  ): AppAlert {
    return {
      key,
      uniqueAlertId,
      type: AppAlertType.account,
      alertData: {
        copy: {
          title: alertContent.copy.header ?? '',
          body:
            !!replacements && !!alertContent.copy.body
              ? this.replaceWithValues(alertContent.copy.body, replacements)
              : alertContent.copy.body ?? '',
          closeLabel: !!alertContent.closeAltText
            ? this.replaceWithValues(alertContent.closeAltText, { ban: key })
            : '',
        },
        cta: !!alertContent.cta
          ? [
              {
                copy: alertContent.cta.copy ?? '',
                val: alertContent.cta.href ?? '',
                alt: !!alertContent.cta.alt ? this.replaceWithValues(alertContent.cta.alt, { ban: key }) : '',
              },
            ]
          : [],
        showCloseButton: alertContent.dismissible,
        variant: alertContent.variant,
      },
      analyticsConfig,
      iconName: alertContent.variant === 'info' ? 'info-outlined' : 'error-outlined',
    };
  }

  private getAlertContentById(alerts: AlertContentDetails[], id: string) {
    return alerts?.find(alert => alert.alertId === id);
  }

  private getDiscountBannerAnalyticsConfig() {
    return {
      events: { notification: true },
      page: { name: 'overview', hierarchy: ['myaccount', 'overview'] },
      displayAlertTracking: {
        additionalPayload: {
          notification: [
            {
              id: '',
              type: 'banner',
              name: '',
              message: '',
              position: 'top',
            },
          ],
        },
      },
    };
  }

  private replaceWithValues(content: string, replacements) {
    return content?.replace(/{\s*\w+\s*}/g, keys => {
      const key = keys.replace(/[{}]/g, '').trim();
      const replacement = replacements[key];
      return replacement ? String(replacement) : keys;
    });
  }

  private getSuspensionAnalyticsConfig() {
    return {
      ...earlySuspensionAnalyticsConfig,
      displayAlertTracking: { mappingKey: 'suspensionAlert', additionalPayload: { notification: [] } },
      alertCtaTracking: {
        mappingKey: 'alertCtaTracking',
        additionalPayload: {
          interaction: {
            name: '',
            type: 'click',
          },
        },
      },
    };
  }

  private getAdditionalTrackingPayload(name: string) {
    return {
      notification: [
        {
          id: '',
          type: 'banner',
          name,
          message: '',
          position: 'top',
        },
      ],
    };
  }

  private trackCollectionActivity(collectionActivityCode: string) {
    const payload: ICompleteUniversalDataLayer = {};
    let activityName = 'NA';
    switch (collectionActivityCode) {
      case EarlySuspensionFlag.COLLECTION_CODE_1:
        activityName = 'agency assignment';
        break;
      case EarlySuspensionFlag.COLLECTION_CODE_C:
        activityName = 'cancellation';
        break;
      case EarlySuspensionFlag.COLLECTION_CODE_R:
        activityName = 'balance already assigned';
        break;
    }
    payload.page = {
      name: 'account overview',
      hierarchy: ['myrogers', 'accountoverview'],
    };

    payload.events = { notification: true };
    payload.notification = [
      {
        type: 'data collection alert',
        name: activityName,
        id: '',
        message: 'NA',
        position: 'NA',
      },
    ];
    this.analyticsServiceProxy.trackNotifications(payload.notification, payload);
  }
}
